import { CommonModule, DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, HostListener, Type, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LucideIconsModule } from '../../lucide-icons.module';
import { ExternalViewerDialogService } from './external-viewer-dialog.service';

@Component({
    selector: 'app-external-viewer-dialog',
    standalone: true,
    imports: [CommonModule, LucideIconsModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './external-viewer-dialog.component.html',
    styleUrls: ['./external-viewer-dialog.component.scss'],
})
export class ExternalViewerDialogComponent {
    readonly dialog = inject(ExternalViewerDialogService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly doc = inject(DOCUMENT);
    private readonly cdr = inject(ChangeDetectorRef);

    viewerComponent: Type<unknown> | null = null;
    loadError: string | null = null;
    private previousBodyOverflow: string | null = null;

  constructor() {
    this.dialog.state$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        if (state) {
          this.lockScroll();
          this.loadError = null;
          void this.ensureViewerLoaded();
        } else {
          this.unlockScroll();
        }
        this.cdr.markForCheck();
      });
  }

    @HostListener('document:keydown.escape')
    onEscape() {
        if (this.dialog.isOpen()) this.dialog.close();
    }

    close(event?: Event): void {
        event?.stopPropagation();
        this.dialog.close();
    }

    retryLoad(): void {
        this.loadError = null;
        void this.ensureViewerLoaded(true);
        this.cdr.markForCheck();
    }

    private lockScroll(): void {
        if (!this.doc?.body) return;
        if (this.previousBodyOverflow !== null) return;
        this.previousBodyOverflow = this.doc.body.style.overflow || '';
        this.doc.body.style.overflow = 'hidden';
    }

    private unlockScroll(): void {
        if (!this.doc?.body) return;
        if (this.previousBodyOverflow === null) return;
        this.doc.body.style.overflow = this.previousBodyOverflow;
        this.previousBodyOverflow = null;
    }

  private async ensureViewerLoaded(force = false): Promise<void> {
    if (this.viewerComponent && !force) return;
    try {
      const module = await import('../../../pages/viewer/external-viewer-page.component');
      this.viewerComponent = module.ExternalViewerPageComponent;
      this.loadError = null;
    } catch (err: unknown) {
      const msg = ((err as Error)?.message || '').toString();
      const isChunkError =
        (err as Error)?.name === 'ChunkLoadError' ||
        msg.includes('ChunkLoadError') ||
        msg.includes('Loading chunk');
      if (isChunkError) {
        const reloadKey = 'chunk-reload-attempted';
        if (!sessionStorage.getItem(reloadKey)) {
          sessionStorage.setItem(reloadKey, '1');
          window.location.reload();
          return;
        }
        sessionStorage.removeItem(reloadKey);
      }
      console.warn('Failed to load external viewer component:', err);
      this.viewerComponent = null;
      this.loadError = 'Could not open this file viewer.';
    } finally {
      this.cdr.markForCheck();
    }
  }
}
